
import {
  ref,
  watch,
  nextTick,
  computed,
  PropType,
  onMounted,
  onBeforeUpdate,
  defineComponent,
  ComponentPublicInstance,
} from 'vue';
import {TopMenuItem} from './types';
import useResize from '../../../composables/useResize';
import NavigationLink from '@ohrm/oxd/core/components/Topbar/NavigationLink.vue';
import NavigationMore from '@ohrm/oxd/core/components/Topbar/NavigationMore.vue';

export default defineComponent({
  name: 'oxd-navigation',

  props: {
    menuItems: {
      type: Object as PropType<TopMenuItem[]>,
    },
  },

  components: {
    'oxd-navigation-link': NavigationLink,
    'oxd-navigation-more': NavigationMore,
  },

  setup(props) {
    const lastMenuItemWidth = ref(0);
    const navbar = ref<HTMLElement>();
    const visibleMenuItems = ref(props.menuItems);
    const hiddenMenuItems = ref<TopMenuItem[]>([]);
    const menuItemsRefs = ref<ComponentPublicInstance[]>([]);
    const {width} = useResize(navbar);

    const setMenuItem = ($el: ComponentPublicInstance | null) => {
      if ($el) {
        menuItemsRefs.value.push($el);
      }
    };

    const menuClasses = computed(() => {
      return props.menuItems.map(item => {
        return {
          'oxd-topbar-body-nav-tab': true,
          '--parent': item.children.length > 0,
          '--visited': item.active,
        };
      });
    });

    const getHTMLElementWidth = ($el: HTMLElement) => {
      const elStyles = window.getComputedStyle($el);
      return $el.clientWidth + parseInt(elStyles.marginRight);
    };

    const arrangeMenuItems = (menuItems: ComponentPublicInstance[]) => {
      // offset the width of "more" element
      const widthOffset = hiddenMenuItems.value.length > 0 ? 80 : 0;
      const menuItemsWidth = menuItems.reduce((acc, menuItem) => {
        return acc + getHTMLElementWidth(menuItem.$el);
      }, widthOffset);

      // if total with of menu items is greater than nav bar
      // remove last menu, add it to hidden menu + store it's width
      if (menuItemsWidth > width.value) {
        lastMenuItemWidth.value = getHTMLElementWidth(
          menuItems[menuItems.length - 1].$el,
        );
        hiddenMenuItems.value.unshift(visibleMenuItems.value.pop());
      }

      if (
        hiddenMenuItems.value.length > 0 &&
        width.value > menuItemsWidth + lastMenuItemWidth.value
      ) {
        nextTick().then(() => {
          visibleMenuItems.value.push(hiddenMenuItems.value.shift());
        });
      }
    };

    // trigger menu side effects. ie: adding/removing items
    watch(menuItemsRefs, arrangeMenuItems);

    // arrange menu items once after dom loaded
    onMounted(async () => {
      await nextTick();
      arrangeMenuItems(menuItemsRefs.value);
    });

    onBeforeUpdate(() => {
      // reset menu refs array as vue re-render
      menuItemsRefs.value = [];
    });

    return {
      width,
      navbar,
      setMenuItem,
      menuClasses,
      menuItemsRefs,
      hiddenMenuItems,
      visibleMenuItems,
    };
  },
});
